import { useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { TicketSpalte } from "@kundeninfo/api-client";
import { Feld, Modal, ModalFuss, darfAbbrechen, useBestaetigung } from "@kundeninfo/ui";
import { Auswahl, Formular, Hinweis, Optionen } from "@kundeninfo/ui/me-design";
import { KiKnopf, LoeschKnopf } from "../../components/meHuellen.js";
import type { TicketsClient } from "./ticketsClient.js";

/** Spalte anlegen oder bearbeiten (Spec „Board › Aufbau"): strikter Dialog (Abbrechen mit
 *  Rückfrage bei ungespeicherter Eingabe), Löschen nur leer mit gestapelter Gefahr-Rückfrage,
 *  Reihenfolge über „Nach links“/„Nach rechts“ (F44). Das Umordnen schreibt ohne Version
 *  (letzter gewinnt, Review Aufgabe 6) — das Board lädt danach vom Server neu, statt die
 *  Reihenfolge lokal zu setzen. */

type Art = "OPEN" | "WAITING" | "DONE";
type Leiste = "COLLAPSED" | "EXPANDED" | "NONE";

export function SpaltenDialog(p: {
  spalte: TicketSpalte | null; spalten: readonly TicketSpalte[]; client: TicketsClient;
  onFertig: () => void; onAbbrechen: () => void;
  /** Nach „Nach links“/„Nach rechts“: das Board lädt vom Server nach, der Dialog bleibt offen. */
  onNeuLaden: () => void | Promise<void>;
  /** Dichte der Zeitleiste auf diesem Gerät (Entscheid 155); nur mit gesetztem Wert zeigt der Dialog das Feld. */
  dichteOffen?: boolean | undefined; onDichte?: (offen: boolean) => void;
}): ReactElement {
  const [name, setName] = useState(p.spalte?.name ?? "");
  const [art, setArt] = useState<Art>(p.spalte?.stateKind ?? "OPEN");
  const [leiste, setLeiste] = useState<Leiste>(p.spalte?.timelineMode ?? "COLLAPSED");
  const [dichte, setDichte] = useState(p.dichteOffen ?? false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const { bestaetigen } = useBestaetigung();

  async function ausfuehren(arbeit: () => Promise<unknown>, danach: () => void | Promise<void> = p.onFertig):
      Promise<void> {
    setLaeuft(true);
    setFehler(null);
    try { await arbeit(); await danach(); }
    catch (e) { setFehler(e instanceof ApiError ? (e.fields?.[0]?.message ?? e.message) : "Das ging nicht."); }
    finally { setLaeuft(false); }
  }

  const geaendert = name !== (p.spalte?.name ?? "") || art !== (p.spalte?.stateKind ?? "OPEN")
    || leiste !== (p.spalte?.timelineMode ?? "COLLAPSED") || dichte !== (p.dichteOffen ?? false);
  async function abbrechen(): Promise<void> {
    if (laeuft) return;
    if (!(await darfAbbrechen(geaendert))) return;
    p.onAbbrechen();
  }
  const position = p.spalte === null ? -1 : p.spalten.findIndex((s) => s.id === p.spalte!.id);
  // Vorab gesperrt, was der Server ohnehin abwiese (Spaltenrepository): Statusart nur bei
  // leerer Spalte (auch archivierte zählen), nie an der Eingangsspalte und nie an der letzten
  // „erledigt“-Spalte; Löschen ebenso.
  const letzteErledigt = p.spalte?.stateKind === "DONE" && p.spalten.filter((s) => s.stateKind === "DONE").length <= 1;
  const statusGrund = p.spalte === null ? null
    : p.spalte.isInbox ? "Die Eingangsspalte bleibt offen."
    : p.spalte.ticketCount > 0 ? "Die Statusart lässt sich nur an einer leeren Spalte ändern."
    : letzteErledigt ? "Es muss immer eine Spalte „erledigt“ geben."
    : null;
  const loeschGrund = p.spalte === null ? null
    : p.spalte.isInbox ? "Die Eingangsspalte lässt sich nicht löschen."
    : p.spalte.ticketCount > 0 ? "Löschen geht nur bei einer leeren Spalte (auch archivierte Tickets zählen)."
    : letzteErledigt ? "Die letzte Spalte „erledigt“ lässt sich nicht löschen."
    : null;
  const nameSetzen = (w: string) => { setName(w); setFehler(null); };

  const eingabe = { name, stateKind: art, timelineMode: art === "DONE" ? "NONE" as const : leiste };
  const verschieben = (richtung: -1 | 1) => ausfuehren(async () => {
    const ids = p.spalten.map((s) => s.id);
    const i = ids.indexOf(p.spalte!.id);
    const j = i + richtung;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j]!, ids[i]!];
    await p.client.orderTicketColumns(ids);
  }, p.onNeuLaden);

  return (
    <Modal titel={p.spalte === null ? "Neue Spalte" : `Spalte „${p.spalte.name}“`} onAbbrechen={() => { void abbrechen(); }}
      breite="standard" abbrechenGesperrt={laeuft}>
      <Formular>
        <Feld id="spalte-name" label="Name" wert={name} onChange={nameSetzen} bearbeitbar pflicht max={60} />
        <Auswahl beschriftung="Statusart" wert={art} onGeaendert={(d) => { setArt(d.wert as Art); setFehler(null); }}
          {...(statusGrund !== null ? { gesperrt: true, hilfe: statusGrund } : {})}>
          <option value="OPEN">offen</option>
          <option value="WAITING">wartend</option>
          <option value="DONE">erledigt</option>
        </Auswahl>
        <Optionen beschriftung="Zeitleiste" darstellung="segmente" richtung="waagerecht"
          wert={art === "DONE" ? "NONE" : leiste} {...(art === "DONE" ? { gesperrt: true } : {})}
          onGeaendert={(d) => { if (d.wert !== undefined) { setLeiste(d.wert as Leiste); setFehler(null); } }}>
          <option value="COLLAPSED">zu</option>
          <option value="EXPANDED">offen</option>
          <option value="NONE">ohne</option>
        </Optionen>
        {p.spalte !== null && p.dichteOffen !== undefined && art !== "DONE" && leiste !== "NONE" && (
          <Optionen beschriftung="Leere Tage auf diesem Gerät" darstellung="segmente" richtung="waagerecht"
            wert={dichte ? "offen" : "schmal"} hilfe="Gilt nur in diesem Browser; die Vorgabe der Spalte steht unter „Zeitleiste“."
            onGeaendert={(d) => { if (d.wert !== undefined) setDichte(d.wert === "offen"); }}>
            <option value="schmal">schmal</option>
            <option value="offen">mit Ablagefläche</option>
          </Optionen>
        )}
      </Formular>
      {p.spalte !== null && (
        <div className="tb__spaltenaktionen">
          <KiKnopf deaktiviert={laeuft || position <= 0} onClick={() => { void verschieben(-1); }}>Nach links</KiKnopf>
          <KiKnopf deaktiviert={laeuft || position < 0 || position >= p.spalten.length - 1}
            onClick={() => { void verschieben(1); }}>Nach rechts</KiKnopf>
          <LoeschKnopf name="Spalte löschen" deaktiviert={laeuft || loeschGrund !== null} onClick={() => { void (async () => {
            if (!(await bestaetigen({ titel: "Spalte löschen?", text: `Die Spalte „${p.spalte!.name}“ wirklich löschen?`,
                                      bestaetigenText: "Löschen", gefaehrlich: true }))) return;
            await ausfuehren(() => p.client.deleteTicketColumn(p.spalte!.id));
          })(); }} />
          {loeschGrund !== null && <span className="tb__spaltengrund">{loeschGrund}</span>}
        </div>
      )}
      {fehler !== null && <Hinweis art="fehler">{fehler}</Hinweis>}
      <ModalFuss speichernText="Speichern" speichernGesperrt={laeuft || name.trim() === ""}
        onSpeichern={() => { void ausfuehren(async () => {
          await (p.spalte === null ? p.client.createTicketColumn(eingabe)
            : p.client.updateTicketColumn(p.spalte.id, p.spalte.version, eingabe));
          if (p.spalte !== null && p.dichteOffen !== undefined && dichte !== p.dichteOffen) p.onDichte?.(dichte);
        }); }}
        onAbbrechen={() => { void abbrechen(); }} abbrechenGesperrt={laeuft} />
    </Modal>
  );
}
